import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { StudySubjectListSource } from '@/clinical-study/subjects/useStudySubjectList';
import { Button } from '@/components/ui/button';
import { ChoiceSelect } from '@/components/ui/choice-select';
import { FormFeedback } from '@/components/ui/form-feedback';
import { todayInRome } from './calendar-dates';
import {
  buildCalendarItems,
  filterItemsBySubject,
  subjectCode,
  type CalendarSubject,
} from './calendar-items';
import { ClinicalCalendar } from './ClinicalCalendar';
import { useStudyCalendarData } from './useStudyCalendarData';

/**
 * The calendar agenda's reads and presentation (#893), shared by the study page's «Calendario»
 * and {@link StudyCalendarAgenda} in the subject panel — both hand in the page's own subject list
 * and permissions, which the page reads once. Subjects appear by code only. A subject whose schedule could not be
 * read is named, with «Riprova», while the others show; a read the caller's role may not make
 * contributes nothing and says nothing.
 *
 * With `subjectId` the agenda holds that subject alone: no subject filter, no free pool dates, and
 * the subject's schedule is read whatever state the list row reports, since the row may be older
 * than the panel around it.
 */
export function CalendarAgendaView({
  studyId,
  subjectList,
  canReadSchedules,
  canReadPool,
  subjectId,
  onOpenSubject,
  reportListError,
}: {
  studyId: string;
  subjectList: StudySubjectListSource;
  canReadSchedules: boolean;
  canReadPool: boolean;
  subjectId?: string;
  onOpenSubject?: (subjectId: string) => void;
  /** Off where another section already reports a failed subject list. */
  reportListError: boolean;
}) {
  const { t } = useTranslation('clinical-study');
  const [subjectFilter, setSubjectFilter] = useState('');
  // A subject filter belongs to one study: another study starts unfiltered.
  useEffect(() => setSubjectFilter(''), [studyId]);
  const subjects = useMemo((): readonly CalendarSubject[] | null => {
    if (subjectList.status !== 'ready') return null;
    if (subjectId === undefined) return subjectList.subjects;
    const row = subjectList.subjects.find((subject) => subject.id === subjectId);
    return row === undefined ? [] : [row];
  }, [subjectList, subjectId]);
  const single = subjectId !== undefined;
  const reservedKnown = (subjects ?? []).some(
    (subject) => subject.reservedTreatmentDatePoolEntryId !== null,
  );
  const data = useStudyCalendarData({
    studyId,
    subjects,
    canReadSchedules,
    // One subject needs the pool only to resolve its reserved date.
    canReadPool: canReadPool && (!single || reservedKnown),
    readEveryState: single,
    // Every completed list read (after a write in the subject panel, too) re-reads the schedules
    // and the pool in place.
    refreshToken: subjectList.status === 'ready' ? (subjectList.version ?? 0) : 0,
  });
  const today = todayInRome();

  const items = useMemo(
    () =>
      filterItemsBySubject(
        buildCalendarItems({
          subjects: subjects ?? [],
          schedules: data.schedules,
          pool: data.pool,
          today,
          includePool: !single,
        }),
        subjectFilter === '' ? null : subjectFilter,
      ),
    [subjects, data.schedules, data.pool, today, subjectFilter, single],
  );

  const codeById = new Map((subjects ?? []).map((subject) => [subject.id, subjectCode(subject)]));
  const failedCodes = data.failedSubjectIds.map((id) => codeById.get(id) ?? id);

  return (
    <div className="space-y-4" aria-busy={data.refreshing}>
      {data.refreshing && (
        <p role="status" className="text-sm text-muted-foreground">
          {t('calendar.refreshing')}
        </p>
      )}
      {reportListError && subjectList.status === 'error' && (
        <div className="space-y-2">
          <FormFeedback tone="error">{t('calendar.subjectsFailed')}</FormFeedback>
          <Button type="button" variant="outline" className="min-h-11" onClick={subjectList.reload}>
            {t('calendar.retry')}
          </Button>
        </div>
      )}
      {!single && subjects !== null && subjects.length > 0 && (
        <div className="max-w-xs space-y-1">
          <label className="text-sm font-medium" htmlFor="study-calendar-subject-filter">
            {t('calendar.subjectFilterLabel')}
          </label>
          <ChoiceSelect
            id="study-calendar-subject-filter"
            value={subjectFilter}
            onChange={setSubjectFilter}
            itemClassName="min-h-11"
            choices={[
              { value: '', label: t('calendar.subjectFilterAll') },
              ...subjects.map((subject) => ({ value: subject.id, label: subjectCode(subject) })),
            ]}
          />
        </div>
      )}

      {data.loading && subjectList.status !== 'error' && (
        <div role="status" className="space-y-1 text-sm text-muted-foreground">
          {data.total > 0 ? (
            <>
              <p>{t('calendar.loadingProgress', { settled: data.settled, total: data.total })}</p>
              <progress
                className="h-2 w-full max-w-xs"
                max={data.total}
                value={data.settled}
                aria-label={t('calendar.loadingProgress', {
                  settled: data.settled,
                  total: data.total,
                })}
              />
            </>
          ) : (
            <p>{t('calendar.loading')}</p>
          )}
        </div>
      )}

      {!data.loading && (failedCodes.length > 0 || data.poolFailed) && (
        <div className="space-y-2">
          {failedCodes.length > 0 && (
            <FormFeedback tone="error">
              {t('calendar.failedSubjects', {
                count: failedCodes.length,
                codes: failedCodes.join(', '),
              })}
            </FormFeedback>
          )}
          {data.poolFailed && <FormFeedback tone="error">{t('calendar.poolFailed')}</FormFeedback>}
          <Button type="button" variant="outline" className="min-h-11" onClick={data.retry}>
            {t('calendar.retry')}
          </Button>
        </div>
      )}

      {/* A failed subject list is reported above (or by the subjects section): never an empty
          month that reads as «no items». */}
      {subjectList.status !== 'error' && (
        <ClinicalCalendar
          items={items}
          {...(onOpenSubject === undefined ? {} : { onOpenSubject })}
          idPrefix={single ? `subject-${subjectId}` : 'study'}
          loading={data.loading}
        />
      )}
    </div>
  );
}

/**
 * The agenda of a study, or of one of its subjects with `subjectId` (#893). The subject list and
 * the two permission flags come from the page, which owns their single reads (passed down through
 * `StudySubjectsSection` and `SubjectWorkspacePanel`), so the panel issues no second list or
 * permissions request and follows the page's list reloads. `reportListError` is off where the
 * host already reports a failed list — the subject panel lives inside `StudySubjectsSection`,
 * which does.
 */
export function StudyCalendarAgenda({
  studyId,
  subjectId,
  subjectList,
  canReadSchedules,
  canReadPool,
  reportListError,
}: {
  studyId: string;
  subjectId?: string;
  subjectList: StudySubjectListSource;
  canReadSchedules: boolean;
  canReadPool: boolean;
  reportListError: boolean;
}) {
  return (
    <CalendarAgendaView
      studyId={studyId}
      subjectList={subjectList}
      canReadSchedules={canReadSchedules}
      canReadPool={canReadPool}
      {...(subjectId === undefined ? {} : { subjectId })}
      reportListError={reportListError}
    />
  );
}
